import { NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { House, ArrowsLeftRight, Target, TrendUp, User, SignOut, ChartBar, Bell, Gear, ListChecks, Warning, Clock, CalendarBlank, ArrowRight, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import WhatsAppFloatingButton from './WhatsAppFloatingButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [dismissedIds, setDismissedIds] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (error) {
        // User not authenticated
      }
    };
    fetchUser();
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true });
      const allNotifs = res.data.notifications || [];
      setNotifications(allNotifs);
      const visible = allNotifs.filter(n => !dismissedIds.includes(n.task_id));
      setNotifCount(visible.length);
      setOverdueCount(visible.filter(n => n.priority === 'overdue').length);
    } catch (error) {
      // silently fail
    }
  }, [dismissedIds]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logout realizado com sucesso');
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: House },
    { path: '/transactions', label: 'Transações', icon: ArrowsLeftRight },
    { path: '/tasks', label: 'Tarefas', icon: ListChecks },
    { path: '/goals', label: 'Metas', icon: Target },
    { path: '/investments', label: 'Investimentos', icon: TrendUp },
    { path: '/reports', label: 'Relatórios', icon: ChartBar }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--neo-bg)' }} data-testid="app-layout">
      {/* Top Navbar - Neumorphic */}
      <nav className="sticky top-0 z-50 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="neo-card-sm flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-arete.png" 
                alt="Aretê" 
                className="h-10 object-contain"
              />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                      isActive
                        ? 'neo-pressed text-[var(--neo-green)]'
                        : 'hover:neo-raised text-[var(--neo-text-light)] hover:text-[var(--neo-green)]'
                    }`
                  }
                >
                  <item.icon size={20} weight="bold" />
                  <span className="text-sm">{item.label}</span>
                </NavLink>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <div className="relative">
                <button 
                  className="neo-icon-btn relative"
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="notifications-button"
                >
                  <Bell size={20} weight="bold" />
                  {notifCount > 0 && (
                    <span
                      className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1 ${overdueCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-[var(--neo-green)]'}`}
                      data-testid="notification-badge"
                    >
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 mt-3 w-[calc(100vw-2rem)] sm:w-96 neo-card z-50 max-h-[420px] overflow-hidden flex flex-col" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-[var(--neo-text-dark)]">Notificações</h3>
                      <div className="flex items-center gap-2">
                        {notifications.filter(n => !dismissedIds.includes(n.task_id)).length > 0 && (
                          <button
                            onClick={() => { setDismissedIds(notifications.map(n => n.task_id)); setNotifCount(0); setOverdueCount(0); }}
                            className="text-xs font-medium text-[var(--neo-text-light)] hover:text-red-500 flex items-center gap-1 transition-colors"
                            data-testid="clear-notifications-btn"
                          >
                            <Trash size={13} />
                            Limpar
                          </button>
                        )}
                      </div>
                    </div>
                    {notifications.filter(n => !dismissedIds.includes(n.task_id)).length === 0 ? (
                      <div className="neo-pressed p-6 text-center">
                        <Bell size={36} className="mx-auto mb-2 text-[var(--neo-text-light)] opacity-40" />
                        <p className="text-sm text-[var(--neo-text-light)]">Tudo em dia!</p>
                        <p className="text-xs text-[var(--neo-text-light)] opacity-60 mt-1">Nenhuma tarefa próxima do vencimento</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-[300px] space-y-2 pr-1 -mr-1">
                        {notifications.filter(n => !dismissedIds.includes(n.task_id)).map((n) => {
                          const isOverdue = n.priority === 'overdue';
                          const isToday = n.priority === 'today';
                          const isTomorrow = n.priority === 'tomorrow';
                          const PIcon = isOverdue ? Warning : isToday ? Clock : CalendarBlank;
                          const borderColor = isOverdue ? 'border-l-red-500' : isToday ? 'border-l-amber-400' : isTomorrow ? 'border-l-blue-400' : 'border-l-gray-300';
                          const iconColor = isOverdue ? 'text-red-500' : isToday ? 'text-amber-500' : isTomorrow ? 'text-blue-500' : 'text-gray-400';
                          const bgColor = isOverdue ? 'bg-red-50' : isToday ? 'bg-amber-50/50' : '';

                          return (
                            <button
                              key={n.task_id}
                              onClick={() => { setShowNotifications(false); navigate('/tasks'); }}
                              className={`w-full text-left p-3 rounded-lg border-l-4 ${borderColor} ${bgColor} hover:bg-[var(--neo-bg-dark)] transition-all group`}
                              data-testid={`notification-item-${n.task_id}`}
                            >
                              <div className="flex items-start gap-2.5">
                                <PIcon size={18} weight="bold" className={`${iconColor} mt-0.5 shrink-0`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-sm font-semibold text-[var(--neo-text)] truncate">{n.title}</span>
                                    <ArrowRight size={12} className="text-[var(--neo-text-light)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </div>
                                  <p className={`text-xs mt-0.5 font-medium ${isOverdue ? 'text-red-600' : isToday ? 'text-amber-600' : 'text-[var(--neo-text-light)]'}`}>
                                    {n.message}
                                  </p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {notifications.filter(n => !dismissedIds.includes(n.task_id)).length > 0 && (
                      <button
                        onClick={() => { setShowNotifications(false); navigate('/tasks'); }}
                        className="mt-3 w-full text-center text-xs font-semibold text-[var(--neo-green)] hover:underline py-1.5"
                        data-testid="notification-view-all"
                      >
                        Ver todas as tarefas
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button 
                  className="neo-icon-btn overflow-hidden"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-testid="user-menu-button"
                >
                  {user?.picture ? (
                    <Avatar className="w-full h-full">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="text-sm bg-[var(--neo-green)] text-white">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <User size={20} weight="bold" />
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-56 neo-card z-50">
                    <div className="mb-3 pb-3 border-b border-[var(--neo-shadow-dark)]">
                      <p className="font-semibold text-[var(--neo-text-dark)]">{user?.name || 'Usuário'}</p>
                      <p className="text-xs text-[var(--neo-text-light)]">{user?.email}</p>
                    </div>
                    <div className="space-y-1">
                      <button 
                        onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                        className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-[var(--neo-bg)] transition-all text-left"
                        data-testid="menu-profile"
                      >
                        <User size={18} className="text-[var(--neo-green)]" />
                        <span className="text-sm">Perfil</span>
                      </button>
                      <button 
                        onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                        className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-[var(--neo-bg)] transition-all text-left"
                        data-testid="menu-settings"
                      >
                        <Gear size={18} className="text-[var(--neo-gold)]" />
                        <span className="text-sm">Configurações</span>
                      </button>
                      <div className="neo-divider" />
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-[var(--neo-red)]/10 transition-all text-left text-[var(--neo-red)]"
                        data-testid="menu-logout"
                      >
                        <SignOut size={18} />
                        <span className="text-sm font-medium">Sair</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Neumorphic */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4">
        <div className="neo-card-sm">
          <div className="flex items-center justify-around">
            {navItems.slice(0, 5).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex flex-col items-center space-y-1 p-2 rounded-xl transition-all ${
                    isActive
                      ? 'neo-pressed text-[var(--neo-green)]'
                      : 'text-[var(--neo-text-light)]'
                  }`
                }
              >
                <item.icon size={22} weight="bold" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-24 md:pb-8 px-4">
        <div className="max-w-7xl mx-auto py-6">
          {children}
        </div>
      </main>

      {/* WhatsApp Floating Button */}
      <WhatsAppFloatingButton user={user} />

      {/* Click outside to close menus */}
      {(showNotifications || showUserMenu) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setShowNotifications(false); setShowUserMenu(false); }}
        />
      )}
    </div>
  );
};

export default Layout;
